import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useChannels } from "@/hooks/useChannels";
import { StatusBadge, BillingBadge, timeAgo, formatDate } from "@/components/shared";
import { Search, Send, Check, CheckCheck, Clock, AlertCircle, UserPlus, Phone, Radio, MessagesSquare, FileText, Paperclip, Image as ImageIcon, File, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const StatusIcon = ({ status }) => {
  if (status === "pending") return <Clock className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "sent") return <Check className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-[#003399]" />;
  if (status === "failed") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  return null;
};

export function extractPreviewText(msg) {
  // For messages (have display field)
  if (msg?.display?.text_body) return msg.display.text_body;
  if (msg?.display?.media_type) return `[${msg.display.media_type}]`;
  // For conversations (have last_message_display field)
  if (msg?.last_message_display?.text_body) return msg.last_message_display.text_body;
  if (msg?.last_message_display?.media_type) return `[${msg.last_message_display.media_type}]`;
  return `[${msg?.message_type || "?"}]`;
}

function simpleMarkdown(text) {
  if (!text) return text;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    const parts = [];
    let remaining = line;
    let key = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/^(.*?)\*([^*]+)\*(.*)/s);
      const italicMatch = remaining.match(/^(.*?)_([^_]+)_(.*)/s);
      let firstMatch = null;
      let matchType = "";
      if (boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index)) {
        firstMatch = boldMatch;
        matchType = "bold";
      } else if (italicMatch) {
        firstMatch = italicMatch;
        matchType = "italic";
      }
      if (!firstMatch) {
        if (remaining) parts.push(remaining);
        break;
      }
      const before = firstMatch[1];
      const content = firstMatch[2];
      const after = firstMatch[3];
      if (before) parts.push(before);
      if (matchType === "bold") {
        parts.push(<strong key={key++}>{content}</strong>);
      } else {
        parts.push(<em key={key++}>{content}</em>);
      }
      remaining = after;
    }
    return <span key={li}>{parts}{li < lines.length - 1 && <br />}</span>;
  });
}

export const MessageContent = ({ message_type, display }) => {
  if (!display) return <p className="text-sm text-neutral-500 italic">[{message_type}]</p>;

  const parts = [];

  if (display.media_link) {
    if (display.media_type === "image") {
      parts.push(<img key="media" src={display.media_link} alt="Media" className="rounded-md max-h-48 object-cover w-full" />);
    } else if (display.media_type === "video") {
      parts.push(<video key="media" src={display.media_link} controls className="rounded-md max-h-48 w-full" />);
    } else if (display.media_type === "document") {
      parts.push(
        <div key="media" className="flex items-center gap-2 text-sm text-neutral-700">
          <FileText className="w-4 h-4" /> {display.filename || "Dokumen"}
        </div>
      );
    } else if (display.media_type === "audio") {
      parts.push(
        <div key="media" className="flex items-center gap-2 text-sm text-neutral-700">
          <Phone className="w-4 h-4" /> Pesan suara
        </div>
      );
    }
  }

  if (display.text_body) {
    parts.push(
      <p key="text" className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed break-words">
        {simpleMarkdown(display.text_body)}
      </p>
    );
  }

  if (parts.length === 0) {
    return <p className="text-sm text-neutral-500 italic">[{message_type}]</p>;
  }

  return <div className="space-y-1">{parts}</div>;
};

export default function Conversations() {
  const channels = useChannels();
  const [channelFilter, setChannelFilter] = useState("all");
  const [q, setQ] = useState("");
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [compose, setCompose] = useState("");
  const [sending, setSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const fileInputRef = useRef(null);
  const timelineRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const params = { limit: 50, q };
    if (channelFilter !== "all") params.channel_id = channelFilter;
    const res = await api.get("/conversations", params);
    setConvos(res.data || []);
  }, [q, channelFilter]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const openConvo = async (c) => {
    setActive(c);
    const [msgs, det] = await Promise.all([
      api.get(`/conversations/${c.id}/messages`),
      api.get(`/conversations/${c.id}`),
    ]);
    setMessages(msgs.data || []);
    setDetail(det.data);
  };

  useEffect(() => {
    if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [messages]);

  const send = async (content, template_id = null, messageType = "text") => {
    if (!active) return;
    setSending(true);
    try {
      let finalContent = content;
      
      // If there's an attached file, upload it first
      if (attachedFile) {
        const formData = new FormData();
        formData.append("file", attachedFile);
        
        try {
          const uploadRes = await api.post(`/conversations/${active.id}/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          
          const fileUrl = uploadRes.data?.url;
          if (fileUrl) {
            if (attachmentType === "image") {
              finalContent = { image: { link: fileUrl, caption: compose || "" } };
              messageType = "image";
            } else if (attachmentType === "video") {
              finalContent = { video: { link: fileUrl, caption: compose || "" } };
              messageType = "video";
            } else if (attachmentType === "audio") {
              finalContent = { audio: { link: fileUrl } };
              messageType = "audio";
            } else {
              finalContent = { document: { link: fileUrl, filename: attachedFile.name, caption: compose || "" } };
              messageType = "document";
            }
          }
        } catch (uploadErr) {
          toast.error("Gagal mengupload file.");
          setSending(false);
          return;
        }
      }
      
      await api.post(`/conversations/${active.id}/messages`, { 
        message_type: messageType, 
        content: finalContent, 
        template_id 
      });
      setCompose("");
      setAttachedFile(null);
      setAttachmentType(null);
      const msgs = await api.get(`/conversations/${active.id}/messages`);
      setMessages(msgs.data || []);
      loadConvos();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      toast.error("Ukuran file maksimal 16MB.");
      return;
    }

    setAttachedFile(file);
    
    if (file.type.startsWith("image/")) {
      setAttachmentType("image");
    } else if (file.type.startsWith("video/")) {
      setAttachmentType("video");
    } else if (file.type.startsWith("audio/")) {
      setAttachmentType("audio");
    } else {
      setAttachmentType("document");
    }
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setAttachmentType(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getAttachmentIcon = () => {
    if (attachmentType === "image") return <ImageIcon className="w-4 h-4" />;
    if (attachmentType === "video") return <File className="w-4 h-4" />;
    if (attachmentType === "audio") return <Phone className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const saveContact = async () => {
    try {
      await api.post(`/conversations/${active.id}/save-contact`, { name: active.participant_name });
      toast.success("Kontak berhasil disimpan.");
      openConvo(active);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const channelName = (id) => channels.find((c) => c.id === id)?.name || "";

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] lg:grid-cols-[320px_1fr_300px] h-[calc(100vh-4rem)] overflow-hidden bg-white">
      {/* Chat list */}
      <div className="border-r border-[#ECECEC] bg-white flex flex-col overflow-hidden">
        <div className="p-3 border-b border-[#ECECEC] space-y-2 flex-shrink-0">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="bg-white" data-testid="conversation-channel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua channel</SelectItem>
              {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari percakapan..." data-testid="conversation-search"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-transparent text-sm bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-[#003399]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convos.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">Tidak ada percakapan.</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => openConvo(c)} data-testid={`conversation-item-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#F0F0F0] hover:bg-neutral-50 flex gap-3 items-center ${active?.id === c.id ? "bg-[#003399]/5" : ""}`}>
              <div className="w-11 h-11 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-semibold flex-shrink-0">
                {(c.participant_name || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center gap-2">
                  <p className="font-medium text-neutral-900 truncate text-sm">{c.participant_name || c.participant_phone_number}</p>
                  <span className="text-xs text-neutral-400 flex-shrink-0">{timeAgo(c.last_message_at)}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <p className="text-xs text-neutral-500 truncate">{extractPreviewText(c) || "—"}</p>
                  {c.unread_count > 0 && <span className="bg-[#FFDA1A] text-[#111111] font-bold text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 flex-shrink-0">{c.unread_count}</span>}
                </div>
                <p className="text-[10px] text-neutral-400 mt-0.5 truncate">{channelName(c.channel_id)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 chat-bg">
            <div className="bg-white rounded-lg p-10 max-w-sm">
              <MessagesSquare className="w-14 h-14 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold text-neutral-800 mb-2">Pilih percakapan</h3>
              <p className="text-sm text-neutral-500">Pilih chat di sebelah kiri untuk melihat timeline pesan.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="h-16 border-b border-[#ECECEC] flex items-center px-4 gap-3 flex-shrink-0 bg-white">
              <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-semibold">
                {(active.participant_name || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900 truncate">{active.participant_name || active.participant_phone_number}</p>
                <p className="text-xs text-neutral-500 font-mono">{active.participant_phone_number}</p>
              </div>
              {!active.contact_id && (
                <Button size="sm" variant="outline" onClick={saveContact} data-testid="save-contact-button"><UserPlus className="w-4 h-4 mr-1.5" /> Simpan Kontak</Button>
              )}
            </div>

            <div ref={timelineRef} className="flex-1 overflow-y-auto chat-bg p-4 space-y-2" data-testid="message-timeline">
              {messages.map((m) => {
                const out = m.direction === "outbound";
                return (
                  <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${out ? "bg-[#FFF3C2]" : "bg-white"}`}>
                      {m.template_name && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A8A00] bg-[#0A8A0015] rounded-sm px-1.5 py-0.5 mb-1">
                          Template · {m.template_name}
                        </span>
                      )}
                      <MessageContent message_type={m.message_type} display={m.display} />
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        {out && m.billing === "paid" && <span className="text-[9px] font-semibold uppercase tracking-wide text-[#003399]/60">Berbayar</span>}
                        <span className="text-[10px] text-neutral-400">{formatDate(m.created_at)}</span>
                        {out && <StatusIcon status={m.status} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-white p-3 border-t border-[#ECECEC] flex-shrink-0">
              {attachedFile && (
                <div className="mb-2 flex items-center gap-2 p-2 bg-[#F0F2F5] rounded-lg">
                  {getAttachmentIcon()}
                  <span className="text-sm text-neutral-700 truncate flex-1">{attachedFile.name}</span>
                  <button onClick={removeAttachment} className="text-neutral-400 hover:text-neutral-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-10 h-10 rounded-full bg-[#F0F2F5] text-neutral-500 flex items-center justify-center hover:bg-[#E8E8E8] flex-shrink-0"
                  title="Lampirkan file"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <textarea value={compose} onChange={(e) => setCompose(e.target.value)} rows={1} placeholder={attachedFile ? "Tambahkan caption..." : "Ketik pesan..."} data-testid="compose-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (compose.trim() || attachedFile) send({ text: compose }); } }}
                  className="flex-1 resize-none rounded-full border border-transparent px-4 py-2.5 text-sm bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-[#003399] max-h-24" />
                <button onClick={() => (compose.trim() || attachedFile) && send({ text: compose })} disabled={sending || (!compose.trim() && !attachedFile)} data-testid="compose-send"
                  className="w-10 h-10 rounded-full bg-[#003399] text-white flex items-center justify-center hover:bg-[#002B80] disabled:opacity-50 flex-shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail panel */}
      <div className="border-l border-[#ECECEC] bg-white overflow-y-auto hidden lg:flex flex-col">
        {detail ? (
          <div className="p-5 space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-neutral-200 flex items-center justify-center text-2xl font-semibold text-neutral-600 mx-auto mb-3">
                {(detail.participant_name || "?").charAt(0)}
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900">{detail.participant_name || "Tanpa Nama"}</h3>
              <p className="text-sm text-neutral-500 font-mono flex items-center justify-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" /> {detail.participant_phone_number}</p>
            </div>
            <div className="border-t border-[#F0F0F0] pt-4 space-y-3">
              <DetailRow icon={Radio} label="Channel" value={detail.channel?.name} />
              <DetailRow icon={UserPlus} label="Kontak" value={detail.contact ? "Tersimpan" : "Belum disimpan"} />
              {detail.contact?.company && <DetailRow icon={FileText} label="Perusahaan" value={detail.contact.company} />}
              {detail.contact?.opt_in_status && (
                <div className="flex items-center gap-3 text-sm">
                  <UserPlus className="w-4 h-4 text-neutral-400" />
                  <span className="text-neutral-500 flex-1">Opt-in</span>
                  <StatusBadge status={detail.contact.opt_in_status} />
                </div>
              )}
            </div>
            {detail.contact?.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {detail.contact.tags.map((t) => <span key={t} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full border border-[#ECECEC]">{t}</span>)}
              </div>
            )}
            {messages.length > 0 && (() => {
              const last = [...messages].reverse().find(m => m.content);
              if (!last) return null;
              return (
                <div className="border-t border-[#F0F0F0] pt-4">
                  <p className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#767676] mb-2">Pesan Terakhir</p>
                  <div className="rounded-lg bg-[#FFF3C2] p-3 border-l-4 border-[#FFDA1A]">
                    {last.template_name && (
                      <p className="text-[10px] font-bold uppercase tracking-[0.05em] text-[#003399] mb-1">Template · {last.template_name}</p>
                    )}
                    <MessageContent message_type={last.message_type} display={last.display} />
                    <p className="text-[10px] text-neutral-400 mt-2">{formatDate(last.created_at)} · {last.direction === "outbound" ? "Terkirim" : "Diterima"}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-neutral-400 p-6 text-center">Detail kontak akan tampil di sini.</div>
        )}
      </div>
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 text-sm">
    <Icon className="w-4 h-4 text-neutral-400" />
    <span className="text-neutral-500 flex-1">{label}</span>
    <span className="font-medium text-neutral-900 truncate">{value || "-"}</span>
  </div>
);
